import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import moment from 'moment';
import { DeleteOutline } from '@mui/icons-material';
import * as Sentry from '@sentry/browser';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import { DateFormat } from '../../../../../utils/date';
import LoadingPage from '../../../../../components/LoadingPage';
import LoadingOverlay from '../../../LoadingOverlay';
import ConfirmDialog from '../../../ConfirmDialog';
import Track from '../../../../../Model/Track';
import TrackAssignment, { TrackAssignmentStatus } from '../../../../../Model/TrackAssignment';
import FirebaseContext from '../../../../../context/FirebaseContext';

import {
  Container,
  TrackCard,
  CardBody,
  CardFooter,
  MetaRow,
  MetaDate,
  TrackName,
  TrackDetail,
  StatusChip,
  RemoveAction,
  RemoveText,
  FilterChip,
  FilterContainer,
  EmptyText,
} from './styles';
import texts from './texts.json';

const TrackAssignments = ({ user, coachId }) => {
  const [assignments, setAssignments] = useState([]);
  const [trackDocs, setTrackDocs] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(
    new Set([TrackAssignmentStatus.ACTIVE]),
  );

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { firebase } = useContext(FirebaseContext);

  const loadTrackAssignments = useCallback(async () => {
    const collection = await TrackAssignment.getTrackAssignmentsByUser(user, coachId);
    const assignmentDocs = collection.hasDocs ? collection.docs : [];

    if (!isComponentMountedRef.current) return;

    setAssignments(assignmentDocs);

    // Only fetch Track docs for legacy assignments that don't have trackName denormalized
    const trackDocMap = {};
    const refsToFetch = new Set();
    assignmentDocs.forEach((assignment) => {
      const { trackId, trackName } = assignment;
      if (trackId && !trackName && !refsToFetch.has(trackId)) {
        refsToFetch.add(trackId);
      }
    });

    if (refsToFetch.size > 0) {
      const promises = [];
      refsToFetch.forEach((trackId) => {
        promises.push(Track.getTrackById(trackId).then((doc) => { trackDocMap[trackId] = doc; }));
      });
      await Promise.all(promises);
    }

    if (isComponentMountedRef.current) {
      setTrackDocs(trackDocMap);
      setIsReady(true);
    }
  }, [user, coachId, isComponentMountedRef]);

  useEffect(() => {
    loadTrackAssignments();
  }, [loadTrackAssignments]);

  const onRemoveClick = useCallback((assignment) => {
    setSelectedAssignment(assignment);
    setShowRemoveConfirm(true);
  }, []);

  const onConfirmRemove = useCallback(async () => {
    if (!selectedAssignment) return;

    setShowRemoveConfirm(false);
    setIsRemoving(true);
    try {
      const response = await firebase.remote('workout-removeTrackAssignment', {
        trackAssignmentId: selectedAssignment.id,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error removing track');
      }

      showToast(texts.removeSuccess);
      setSelectedAssignment(null);
      await loadTrackAssignments();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          trackAssignmentId: selectedAssignment.id,
        },
      });
      showToast(texts.removeError, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsRemoving(false);
      }
    }
  }, [selectedAssignment, firebase, showToast, loadTrackAssignments, isComponentMountedRef]);

  const toggleStatus = useCallback((status) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const assignmentItems = useMemo(() => {
    const filtered = selectedStatuses.size > 0
      ? assignments.filter((a) => selectedStatuses.has(a.status))
      : assignments;

    return filtered.map((assignment) => {
      const currentIndex = assignment.currentProgramIndex + 1;
      const currentProgram = assignment.programs?.[assignment.currentProgramIndex];

      // Use denormalized trackName/programName when available, fall back to fetched Track doc
      const trackDoc = trackDocs[assignment.trackId];
      const name = assignment.trackName ?? trackDoc?.name ?? 'Track';
      const programName = currentProgram?.programName
        ?? trackDoc?.programs?.[assignment.currentProgramIndex]?.programName
        ?? `Program ${currentIndex}`;

      const assignedDate = assignment.assignedAt?.toDate
        ? moment(assignment.assignedAt.toDate()).format(DateFormat.DATE_FORMAT_COMMA)
        : '';

      return {
        id: assignment.id,
        name,
        status: assignment.status,
        currentIndex,
        programName,
        assignedDate,
      };
    });
  }, [assignments, trackDocs, selectedStatuses]);

  if (!isReady) {
    return <LoadingPage />;
  }

  if (!assignments.length) {
    return (
      <Container>
        <EmptyText>{texts.emptyText}</EmptyText>
      </Container>
    );
  }

  return (
    <Container>
      <FilterContainer>
        {Object.values(TrackAssignmentStatus).map((status) => (
          <FilterChip
            key={status}
            label={texts.status[status] || status}
            size="small"
            color={selectedStatuses.has(status) ? 'primary' : 'default'}
            variant={selectedStatuses.has(status) ? 'filled' : 'outlined'}
            onClick={() => toggleStatus(status)}
          />
        ))}
      </FilterContainer>
      {!assignmentItems.length && (
        <EmptyText>{texts.noFilteredTracks}</EmptyText>
      )}
      {assignmentItems.map((assignment) => (
        <TrackCard key={assignment.id}>
          <CardBody>
            <MetaRow>
              <StatusChip
                label={texts.status[assignment.status] || assignment.status}
                status={assignment.status}
                size="small"
              />
              {assignment.assignedDate && (
                <MetaDate>{format(texts.assignedOn, { date: assignment.assignedDate })}</MetaDate>
              )}
            </MetaRow>
            <TrackName>{assignment.name}</TrackName>
            {assignment.programName && (
              <TrackDetail>
                {format(texts.currentProgram, {
                  index: assignment.currentIndex,
                  name: assignment.programName,
                })}
              </TrackDetail>
            )}
          </CardBody>
          {assignment.status === TrackAssignmentStatus.ACTIVE && (
            <CardFooter>
              <RemoveAction onClick={() => onRemoveClick(assignment)}>
                <DeleteOutline fontSize="small" />
                <RemoveText>{texts.removeTrack}</RemoveText>
              </RemoveAction>
            </CardFooter>
          )}
        </TrackCard>
      ))}
      <ConfirmDialog
        isOpen={showRemoveConfirm}
        onConfirm={onConfirmRemove}
        onCancel={() => {
          setShowRemoveConfirm(false);
          setSelectedAssignment(null);
        }}
        dialogTexts={{
          title: texts.removeConfirm.title,
          content: format(texts.removeConfirm.content, {
            trackName: selectedAssignment?.name || '',
          }),
        }}
      />
      <LoadingOverlay isLoading={isRemoving} />
    </Container>
  );
};

TrackAssignments.propTypes = {
  user: PropTypes.string.isRequired,
  coachId: PropTypes.string.isRequired,
};

export default TrackAssignments;
